/**
 * Действие для {@link updateArrayWithAction}: создание/добавление, обновление, удаление (в т.ч. массовые).
 * @template T тип элемента массива
 */
export type ArrayUpdateAction<T> =
    | { type: "create"; payload: { item: T; index?: number } }
    | { type: "createMany"; payload: { items: T[] } }
    | { type: "update"; payload: { item: T } }
    | { type: "updateMany"; payload: { items: T[] } }
    | { type: "delete"; payload: { id: string | number } }
    | { type: "deleteMany"; payload: { ids: (string | number)[] } };

/**
 * Фабрика действий для {@link updateArrayWithAction}.
 */
export const arrayAction = {
    create: <T>(item: T, index?: number): ArrayUpdateAction<T> => ({ type: "create", payload: { item, index } }),
    createMany: <T>(items: T[]): ArrayUpdateAction<T> => ({ type: "createMany", payload: { items } }),
    update: <T>(item: T): ArrayUpdateAction<T> => ({ type: "update", payload: { item } }),
    updateMany: <T>(items: T[]): ArrayUpdateAction<T> => ({ type: "updateMany", payload: { items } }),
    delete: <T>(id: string | number): ArrayUpdateAction<T> => ({ type: "delete", payload: { id } }),
    deleteMany: <T>(ids: (string | number)[]): ArrayUpdateAction<T> => ({ type: "deleteMany", payload: { ids } }),
} as const;

/**
 * Возвращает новый массив в зависимости от типа действия. Исходный массив не мутирует.
 * @template T тип элемента массива
 * @param arr исходный массив
 * @param action действие над массивом
 * @param getId функция получения идентификатора элемента
 */
export function updateArrayWithAction<T>(arr: T[], action: ArrayUpdateAction<T>, getId: (item: T) => string | number): T[] {
    switch (action.type) {
        case "create": {
            const { item, index } = action.payload;
            const i = index ?? arr.length;
            return [...arr.slice(0, i), item, ...arr.slice(i)];
        }
        case "createMany":
            return [...arr, ...action.payload.items];
        case "update": {
            const { item } = action.payload;
            const itemId = getId(item);
            return arr.map((existingItem) => (getId(existingItem) === itemId ? item : existingItem));
        }
        case "updateMany": {
            return action.payload.items.reduce(
                (current, item) => updateArrayWithAction(current, { type: "update", payload: { item } }, getId),
                arr,
            );
        }
        case "delete": {
            const { id: deleteId } = action.payload;
            return arr.filter((existingItem) => getId(existingItem) !== deleteId);
        }
        case "deleteMany": {
            const idSet = new Set(action.payload.ids);
            return arr.filter((existingItem) => !idSet.has(getId(existingItem)));
        }
    }
}
